// src/components/rodeoAi/RodeoChat.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Snackbar,
    Container,
    useMediaQuery,
    useTheme,
    IconButton,
    Skeleton,
    Drawer,
    Fab,
    Zoom
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ForumIcon from '@mui/icons-material/Forum';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import axios from '../../config/axiosConfig';
import { useParams, useNavigate } from 'react-router-dom';
import ChatHeader from './ChatHeader';
import ChatMessage from './ChatMessage';
import QuestionBank from './QuestionBank';
import ContextWarning from './ContextWarning';
import Disclaimer from './Disclaimer';
import ConversationHistory from './ConversationHistory';

const RodeoChat = ({
    user,
    mode = 'full',
    height,
    showHeader = true,
    autoFocus = true
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { conversationId: urlConversationId } = useParams();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // State
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [streamingStatus, setStreamingStatus] = useState('');
    const [error, setError] = useState(null);
    const [conversationId, setConversationId] = useState(null);
    const [contextInfo, setContextInfo] = useState(null);
    const [loadingConversation, setLoadingConversation] = useState(false);

    // Sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile && !isTablet);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    // Credits state
    const [credits, setCredits] = useState({
        balance: 0,
        monthlyAllowance: 0,
        tier: 'free',
        loading: true
    });

    // Warning state
    const [showWarning, setShowWarning] = useState(false);
    const [warningDismissed, setWarningDismissed] = useState(false);

    // Snackbar for copy action
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    // Refs to prevent unnecessary re-renders and race conditions
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const userHasScrolledRef = useRef(false);
    const loadingRef = useRef(false);
    const hasNavigatedRef = useRef(false);
    const initialLoadRef = useRef(false);
    const lastLoadedConvIdRef = useRef(null);

    // Fetch credits on mount
    useEffect(() => {
        fetchCredits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load conversation when URL changes - OPTIMIZED
    useEffect(() => {
        // Skip if already loading or if we just loaded this conversation
        if (loadingRef.current || lastLoadedConvIdRef.current === urlConversationId) {
            return;
        }

        if (urlConversationId) {
            // Load the conversation from URL
            loadConversation(urlConversationId);
        } else if (initialLoadRef.current) {
            // Only reset if we've already had an initial load (prevents clearing on mount)
            handleNewChatInternal();
        }

        initialLoadRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlConversationId]);

    // Update URL when conversationId changes (for new conversations) - OPTIMIZED
    useEffect(() => {
        if (conversationId && !urlConversationId && !hasNavigatedRef.current && !streaming) {
            // Only navigate once per new conversation and not during streaming
            hasNavigatedRef.current = true;
            navigate(`/chat/${conversationId}`, { replace: true });

            // Reset the flag after navigation completes
            setTimeout(() => {
                hasNavigatedRef.current = false;
            }, 100);
        }
    }, [conversationId, urlConversationId, streaming, navigate]);

    // State to trigger conversation list refresh
    const [refreshConversations, setRefreshConversations] = useState(0);

    // Auto-focus input (disabled on mobile to prevent keyboard popup)
    useEffect(() => {
        if (autoFocus && !isMobile && inputRef.current && !loadingConversation) {
            inputRef.current.focus();
        }
    }, [autoFocus, isMobile, loadingConversation]);

    // Check for long conversation warning
    useEffect(() => {
        const messageCount = messages.length;
        if (messageCount >= 20 && messageCount < 30 && !warningDismissed) {
            setShowWarning(true);
        } else if (messageCount >= 30) {
            setShowWarning(true);
            setWarningDismissed(false);
        }
    }, [messages.length, warningDismissed]);

    // Auto-scroll to bottom on new message
    useEffect(() => {
        // Only scroll if the chat container exists AND the user hasn't scrolled up
        if (chatContainerRef.current && !userHasScrolledRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    // Helper to get auth headers
    const getAuthHeaders = useCallback(() => {
        if (user?.token) {
            return { Authorization: `Bearer ${user.token}` };
        }
        const token = localStorage.getItem('token');
        if (token) {
            return { Authorization: `Bearer ${token}` };
        }
        return {};
    }, [user?.token]);

    // Load conversation from backend - OPTIMIZED
    const loadConversation = useCallback(async (convId) => {
        // Prevent duplicate loads
        if (loadingRef.current || lastLoadedConvIdRef.current === convId) {
            return;
        }

        try {
            loadingRef.current = true;
            lastLoadedConvIdRef.current = convId;
            setLoadingConversation(true);
            setError(null);

            const response = await axios.get(`/api/ai/conversations/${convId}`, {
                headers: getAuthHeaders()
            });

            if (response.data) {
                setMessages(response.data.messages || []);
                setConversationId(response.data.conversationId);

                // Set context info if available
                if (response.data.metadata) {
                    setContextInfo({
                        hasContext: true,
                        queryCount: response.data.messages.filter(m => m.role === 'user').length
                    });
                }

                // Check if context is expired
                if (response.data.contextExpired) {
                    setSnackbar({
                        open: true,
                        message: 'Note: This conversation\'s context may have expired. Responses may not reference earlier messages.'
                    });
                }

                // Close mobile drawer after selection
                if (isMobile) {
                    setMobileDrawerOpen(false);
                }
            }
        } catch (error) {
            console.error('Failed to load conversation:', error);
            setError(error.response?.data?.error || 'Failed to load conversation');

            // If conversation not found, redirect to new chat
            if (error.response?.status === 404) {
                lastLoadedConvIdRef.current = null;
                navigate('/chat');
            }
        } finally {
            setLoadingConversation(false);
            loadingRef.current = false;
        }
    }, [getAuthHeaders, isMobile, navigate]);

    // Fetch credits - MEMOIZED
    const fetchCredits = useCallback(async () => {
        try {
            setCredits(prev => ({ ...prev, loading: true }));

            const response = await axios.get('/api/ai/credits', {
                headers: getAuthHeaders()
            });

            setCredits({
                balance: response.data.balance,
                monthlyAllowance: response.data.monthlyAllowance,
                tier: response.data.tier,
                lastReset: response.data.lastReset,
                nextReset: response.data.nextReset,
                upgradeUrl: response.data.upgradeUrl,
                loading: false
            });
        } catch (error) {
            console.error('Failed to fetch credits:', error);
            setCredits(prev => ({ ...prev, loading: false }));
        }
    }, [getAuthHeaders]);

    // Internal new chat handler (doesn't navigate)
    const handleNewChatInternal = useCallback(() => {
        setMessages([]);
        setConversationId(null);
        setContextInfo(null);
        setError(null);
        setWarningDismissed(false);
        setShowWarning(false);
        lastLoadedConvIdRef.current = null;
        hasNavigatedRef.current = false;
    }, []);

    // Handle new chat - MEMOIZED
    const handleNewChat = useCallback(() => {
        handleNewChatInternal();
        navigate('/chat');

        // Close mobile drawer
        if (isMobile) {
            setMobileDrawerOpen(false);
        }
    }, [handleNewChatInternal, navigate, isMobile]);

    // Reset conversation - MEMOIZED
    const resetConversation = useCallback(async () => {
        if (!conversationId) {
            handleNewChat();
            setSnackbar({ open: true, message: 'Started new conversation' });
            return;
        }

        try {
            await axios.post(
                '/api/ai/reset-conversation',
                { conversationId },
                { headers: getAuthHeaders() }
            );

            handleNewChat();
            setSnackbar({ open: true, message: 'Conversation context cleared' });
        } catch (error) {
            console.error('Failed to reset conversation:', error);
            setError(error.response?.data?.error || 'Failed to reset conversation');
        }
    }, [conversationId, handleNewChat, getAuthHeaders]);

    // Send message via streaming - OPTIMIZED
    const sendStreamingMessage = useCallback(async () => {
        if (!input.trim() || streaming || credits.balance === 0) return;

        const userMessage = input;
        setInput('');
        setError(null);
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setStreaming(true);
        setStreamingStatus('');

        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

        try {
            const response = await fetch(`${axios.defaults.baseURL}/api/ai/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                credentials: 'include',
                body: JSON.stringify({
                    message: userMessage,
                    conversationId: conversationId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                for (let i = 0; i < lines.length - 1; i++) {
                    const line = lines[i].trim();

                    if (line.startsWith('data: ')) {
                        const dataStr = line.slice(6);

                        if (dataStr === '[DONE]') {
                            setStreaming(false);
                            setStreamingStatus('');
                            fetchCredits();
                            continue;
                        }

                        try {
                            const data = JSON.parse(dataStr);

                            if (data.type === 'chunk') {
                                if (data.content.startsWith('[STATUS]')) {
                                    const status = data.content.replace('[STATUS]', '').replace('\n', '');
                                    setStreamingStatus(status);
                                } else {
                                    setStreamingStatus('');
                                    setMessages(prev => {
                                        const newMessages = [...prev];
                                        newMessages[newMessages.length - 1].content += data.content;
                                        return newMessages;
                                    });
                                }
                            } else if (data.type === 'metadata') {
                                if (data.conversationId && !conversationId) {
                                    setConversationId(data.conversationId);
                                    // Trigger conversation list refresh for new conversation
                                    setRefreshConversations(prev => prev + 1);
                                }
                                if (data.contextUsed) {
                                    setContextInfo({
                                        hasContext: true,
                                        queryCount: data.contextQueryCount || 1
                                    });
                                }
                            } else if (data.type === 'error') {
                                setError(data.error);
                                setStreaming(false);
                                setStreamingStatus('');
                            }
                        } catch (e) {
                            console.error('Failed to parse SSE data:', e);
                        }
                    }
                }

                buffer = lines[lines.length - 1];
            }
        } catch (err) {
            console.error('Streaming error:', err);
            setError(err.message || 'Failed to stream message');
            setStreamingStatus('');
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setStreaming(false);
            setStreamingStatus('');
        }
    }, [input, streaming, credits.balance, conversationId, getAuthHeaders, fetchCredits]);

    // Handle Enter key - MEMOIZED
    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendStreamingMessage();
        }
    }, [sendStreamingMessage]);

    const handleScroll = () => {
        const element = chatContainerRef.current;
        if (element) {
            // Check if scroll is near the bottom (with a 10px threshold)
            const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 10;
            // If we are not at the bottom, set the ref to true
            userHasScrolledRef.current = !atBottom;
        }
    };

    // Handle question selection - MEMOIZED
    const handleQuestionSelect = useCallback((question) => {
        setInput(question);
        if (inputRef.current && !isMobile) {
            inputRef.current.focus();
        }
    }, [isMobile]);

    // Handle copy message - MEMOIZED
    const handleCopyMessage = useCallback(() => {
        setSnackbar({ open: true, message: 'Message copied to clipboard' });
    }, []);

    // Toggle sidebar (desktop) - MEMOIZED
    const toggleSidebar = useCallback(() => {
        setSidebarOpen(prev => !prev);
    }, []);

    // Toggle mobile drawer - MEMOIZED
    const toggleMobileDrawer = useCallback(() => {
        setMobileDrawerOpen(prev => !prev);
    }, []);

    // Dynamic height calculation - MEMOIZED
    const containerHeight = useMemo(() => {
        if (height) return height;
        if (isMobile) return 'calc(100vh - 120px)';
        if (mode === 'full') return '85vh';
        return '600px';
    }, [height, isMobile, mode]);

    // Loading skeleton for conversation
    if (loadingConversation && messages.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 1, sm: 2, md: 3 } }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 1.5, sm: 2, md: 3 },
                        height: containerHeight,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                    <Box sx={{ flexGrow: 1 }}>
                        {[1, 2, 3].map(i => (
                            <Box key={i} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end' }}>
                                    <Skeleton variant="rounded" width="60%" height={80} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                </Paper>
            </Container>
        );
    }

    // Desktop/Tablet layout with sidebar
    if (!isMobile) {
        return (
            <Box sx={{
                display: 'flex',
                height: containerHeight,
                mt: { xs: 2, sm: 3, md: 4 },
                maxWidth: '100vw',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* Sidebar - Conversation History */}
                {sidebarOpen && (
                    <Box
                        sx={{
                            width: isTablet ? 280 : 320,
                            flexShrink: 0,
                            height: '100%',
                            borderRight: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : '1px solid rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden'
                        }}
                    >
                        <ConversationHistory
                            user={user}
                            currentConversationId={conversationId}
                            onNewChat={handleNewChat}
                            embedded={true}
                            refreshTrigger={refreshConversations}
                        />
                    </Box>
                )}

                {/* Main Chat Area */}
                <Box sx={{
                    flexGrow: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Toggle Sidebar Button */}
                    <IconButton
                        onClick={toggleSidebar}
                        sx={{
                            position: 'absolute',
                            left: 10,
                            top: 10,
                            zIndex: 10,
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(19, 47, 76, 0.8)'
                                    : 'rgba(255, 255, 255, 0.9)',
                            border: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : '1px solid rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(19, 47, 76, 0.9)'
                                        : 'rgba(255, 255, 255, 1)'
                            }
                        }}
                    >
                        {sidebarOpen ? <CloseIcon /> : <ForumIcon />}
                    </IconButton>

                    {/* Chat Content */}
                    <Container
                        maxWidth="lg"
                        sx={{
                            height: '100%',
                            px: { xs: 2, sm: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={(theme) => ({
                                p: { xs: 1.5, sm: 2, md: 3 },
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(19, 47, 76, 0.6)'
                                        : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                border:
                                    theme.palette.mode === 'dark'
                                        ? '1px solid rgba(255, 255, 255, 0.1)'
                                        : '1px solid rgba(0, 0, 0, 0.08)',
                                boxShadow:
                                    theme.palette.mode === 'dark'
                                        ? '0 8px 32px rgba(0,0,0,0.3)'
                                        : '0 4px 24px rgba(0,0,0,0.08)',
                                overflow: 'hidden'
                            })}
                        >
                            {/* Header */}
                            {showHeader && (
                                <ChatHeader
                                    mode={mode}
                                    credits={credits}
                                    onRefreshCredits={fetchCredits}
                                    onResetConversation={resetConversation}
                                    conversationId={conversationId}
                                    contextInfo={contextInfo}
                                />
                            )}

                            {/* Error Alert */}
                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: { xs: 1, sm: 2 } }}
                                    onClose={() => setError(null)}
                                >
                                    {error}
                                </Alert>
                            )}

                            {/* Long Conversation Warning */}
                            {showWarning && (
                                <ContextWarning
                                    messageCount={messages.length}
                                    onStartNew={resetConversation}
                                    onDismiss={() => {
                                        setShowWarning(false);
                                        setWarningDismissed(true);
                                    }}
                                />
                            )}

                            {/* Streaming Status */}
                            {streamingStatus && (
                                <Alert
                                    severity="info"
                                    icon={<CircularProgress size={16} />}
                                    sx={{ mb: { xs: 1, sm: 2 } }}
                                >
                                    {streamingStatus}
                                </Alert>
                            )}

                            {/* Disclaimer & Questions - Show only when no messages */}
                            {messages.length === 0 && (
                                <>
                                    <Disclaimer />
                                    <QuestionBank
                                        onSelectQuestion={handleQuestionSelect}
                                        mode={mode}
                                    />
                                </>
                            )}

                            {/* Messages Area */}
                            <Box
                                ref={chatContainerRef}
                                onScroll={handleScroll}
                                sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    mb: { xs: 1, sm: 2 },
                                    px: { xs: 0.5, sm: 1 }
                                }}
                            >
                                {messages.map((msg, index) => (
                                    <ChatMessage
                                        key={`${conversationId}-${index}`}
                                        message={msg}
                                        isStreaming={streaming && index === messages.length - 1}
                                        onCopy={handleCopyMessage}
                                    />
                                ))}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Input Area */}
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                                <TextField
                                    inputRef={inputRef}
                                    fullWidth
                                    variant="outlined"
                                    placeholder={
                                        credits.balance === 0
                                            ? 'No credits remaining - upgrade to continue'
                                            : 'Ask something... (Enter to send)'
                                    }
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={streaming || credits.balance === 0}
                                    multiline
                                    maxRows={4}
                                    error={credits.balance === 0}
                                    sx={(theme) => ({
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor:
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(19, 47, 76, 0.4)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(10px)'
                                        }
                                    })}
                                />
                                <Button
                                    variant="contained"
                                    onClick={sendStreamingMessage}
                                    disabled={streaming || !input.trim() || credits.balance === 0}
                                    endIcon={streaming ? <CircularProgress size={20} /> : <SendIcon />}
                                >
                                    {streaming ? '' : 'Send'}
                                </Button>
                            </Box>
                        </Paper>
                    </Container>
                </Box>
            </Box>
        );
    }

    // Mobile layout with drawer
    return (
        <>
            {/* Main Chat */}
            <Container maxWidth="md" sx={{ height: containerHeight, py: 1, px: 1 }}>
                <Paper
                    elevation={3}
                    sx={(theme) => ({
                        p: 1.5,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? 'rgba(19, 47, 76, 0.6)'
                                : 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        overflow: 'hidden'
                    })}
                >
                    {showHeader && (
                        <ChatHeader
                            mode="mini"
                            credits={credits}
                            onRefreshCredits={fetchCredits}
                            onResetConversation={resetConversation}
                            conversationId={conversationId}
                            contextInfo={contextInfo}
                        />
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {showWarning && (
                        <ContextWarning
                            messageCount={messages.length}
                            onStartNew={resetConversation}
                            onDismiss={() => {
                                setShowWarning(false);
                                setWarningDismissed(true);
                            }}
                        />
                    )}

                    {streamingStatus && (
                        <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 1 }}>
                            {streamingStatus}
                        </Alert>
                    )}

                    {messages.length === 0 && (
                        <>
                            <Disclaimer />
                            <QuestionBank onSelectQuestion={handleQuestionSelect} mode="mini" />
                        </>
                    )}

                    <Box
                        ref={chatContainerRef}
                        onScroll={handleScroll}
                        sx={{ flexGrow: 1, overflowY: 'auto', mb: 1, px: 0.5 }}>
                        {messages.map((msg, index) => (
                            <ChatMessage
                                key={`${conversationId}-${index}`}
                                message={msg}
                                isStreaming={streaming && index === messages.length - 1}
                                onCopy={handleCopyMessage}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder={credits.balance === 0 ? 'No credits' : 'Ask something...'}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={streaming || credits.balance === 0}
                            multiline
                            maxRows={3}
                            error={credits.balance === 0}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={sendStreamingMessage}
                            disabled={streaming || !input.trim() || credits.balance === 0}
                        >
                            {streaming ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                        </Button>
                    </Box>
                </Paper>
            </Container>

            {/* Floating Action Button */}
            <Zoom in={!mobileDrawerOpen}>
                <Fab
                    color="primary"
                    onClick={toggleMobileDrawer}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                >
                    <HistoryIcon />
                </Fab>
            </Zoom>

            {/* Mobile Drawer */}
            <Drawer
                anchor="left"
                open={mobileDrawerOpen}
                onClose={toggleMobileDrawer}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '85%',
                        maxWidth: 360
                    }
                }}
            >
                <Box sx={{ height: '100%', position: 'relative' }}>
                    <IconButton
                        onClick={toggleMobileDrawer}
                        sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <ConversationHistory
                        user={user}
                        currentConversationId={conversationId}
                        onNewChat={handleNewChat}
                        embedded={true}
                        refreshTrigger={refreshConversations}
                    />
                </Box>
            </Drawer>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ open: false, message: '' })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default RodeoChat;